/**
 * The scan hook: drives a RegionScanSession from either the real camera or a
 * simulated face, and hands the view a live picture of what the engine is doing.
 *
 * The engine is the same module the API and the test suite use, so what you see
 * on screen is not a demonstration of the algorithm — it is the algorithm.
 *
 * Two things changed when the engine went multi-region, and both matter here:
 *
 *   1. A frame is now three RGB regions, not one green number. Both sources
 *      produce the same shape, so the session cannot tell them apart.
 *   2. Frames can be *rejected*. The camera grades every frame for exposure,
 *      motion and face tracking, and a frame that fails is counted but never
 *      appended. The person is told why, live, in words they can act on.
 *
 * The live view reads `session.latest` rather than re-analysing. Analysis means
 * six padded FFTs; doing that every animation frame would burn a phone's battery
 * to redraw a number that only changes once per second anyway.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RegionScanSession, ScanState, SyntheticFace, chrominancePerfusion } from '@omnialera/ori-signal';
import { CAPTURE_FPS, CameraSource, REGION_LAYOUT, describeCameraError } from './camera.js';

/** The simulated subject runs faster than real time so a demo is not a wait. */
const SIMULATION_SPEED = 4;

const OK_ADVICE = Object.freeze({ code: 'ok', severity: 'ok' });

/**
 * A session is "running" while it is IDLE or ACQUIRING.
 *
 * IDLE matters: a fresh session stays IDLE until its first frame lands, so a
 * loop that only ran while ACQUIRING would wait for a state that only the loop
 * itself can produce, and stop after one frame having measured nothing.
 */
const running = (state) => state === ScanState.IDLE || state === ScanState.ACQUIRING;

const IDLE = Object.freeze({
  state: ScanState.IDLE,
  seconds: 0,
  progress: 0,
  samples: 0,
  rejected: 0,
  acceptance: 1,
  bpm: null,
  prominence: null,
  harmonic: null,
  agreement: null,
  spreadBpm: null,
  methodsAgree: null,
  methodSpreadBpm: null,
  byMethod: {},
  perRegion: [],
  perfusion: null,
  result: null,
  advice: OK_ADVICE,
  faceTracked: false,
  luminance: null,
  motion: null,
});

export function useScan({ monkTone, source = 'simulated', videoRef }) {
  const [live, setLive] = useState(IDLE);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  const sessionRef = useRef(null);
  const faceRef = useRef(null);
  const cameraRef = useRef(null);
  const rafRef = useRef(0);
  const carryRef = useRef(0);
  const lastRef = useRef(0);
  const traceRef = useRef(new Float64Array(256));
  const traceLenRef = useRef(0);
  const boxesRef = useRef(REGION_LAYOUT.map((r) => ({ ...r, name: r.name })));
  const smoothRef = useRef({ luminance: null, motion: null });

  /** Latest samples, for the waveform. Read during render; never mutated there. */
  const getTrace = useCallback(() => traceRef.current.subarray(0, traceLenRef.current), []);

  /** Where to draw the region rectangles. Frame fractions, already smoothed. */
  const getRegionBoxes = useCallback(() => boxesRef.current, []);

  /**
   * The pulse band, for the spectrum drawing. Pulled from the session on every
   * animation frame rather than pushed into React state: it is an array of a
   * few hundred numbers changing several times a second, and putting that
   * through a re-render would cost far more than the picture is worth.
   */
  const getSpectrum = useCallback(() => sessionRef.current?.spectrumView?.() ?? null, []);

  const pushTrace = useCallback((value) => {
    const buf = traceRef.current;
    if (traceLenRef.current < buf.length) {
      buf[traceLenRef.current++] = value;
    } else {
      buf.copyWithin(0, 1);
      buf[buf.length - 1] = value;
    }
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  }, []);

  const teardownCamera = useCallback(() => {
    cameraRef.current?.stop();
    cameraRef.current = null;
    setCameraReady(false);
  }, []);

  // Set once the hook is gone. getUserMedia can resolve long after the person
  // navigated away — the permission prompt is modal and unhurried — and a
  // stream handed back to a dead hook was never stopped by anything.
  const goneRef = useRef(false);
  useEffect(
    () => () => {
      goneRef.current = true;
      stopLoop();
      teardownCamera();
    },
    [stopLoop, teardownCamera],
  );

  /** Switching source mid-scan would splice two different signals together. */
  useEffect(() => {
    stopLoop();
    sessionRef.current = null;
    traceLenRef.current = 0;
    setLive(IDLE);
    if (source !== 'camera') teardownCamera();
  }, [source, stopLoop, teardownCamera]);

  const requestingRef = useRef(false);

  const enableCamera = useCallback(async () => {
    // A second request while the first is still waiting on the permission
    // prompt starts a second stream and orphans the first, because the ref can
    // only hold one.
    if (requestingRef.current) return false;
    requestingRef.current = true;
    setError(null);
    try {
      const cam = new CameraSource();
      await cam.start({ videoElement: videoRef?.current ?? undefined });

      // The await above can outlive this component. Handing the stream to a
      // ref nobody will read again leaves the camera on — the indicator light
      // stays lit, with nothing in the app admitting it — until the page is
      // reloaded. For an app whose promise is that no frame leaves the device,
      // a camera running unannounced is the worst possible shape of bug.
      if (goneRef.current) {
        cam.stop();
        return false;
      }

      cameraRef.current = cam;
      boxesRef.current = cam.regionBoxes();
      setCameraReady(true);
      return true;
    } catch (err) {
      if (goneRef.current) return false;
      setError(describeCameraError(err));
      setCameraReady(false);
      return false;
    } finally {
      requestingRef.current = false;
    }
  }, [videoRef]);

  const start = useCallback(() => {
    setError(null);
    const session = new RegionScanSession({ regions: REGION_LAYOUT.length });
    sessionRef.current = session;
    traceLenRef.current = 0;
    carryRef.current = 0;
    lastRef.current = performance.now();
    smoothRef.current = { luminance: null, motion: null };

    if (source === 'simulated') {
      faceRef.current = new SyntheticFace({
        // An unknown-but-plausible resting rate, so the demo is not a magic trick.
        bpm: 56 + Math.floor(Math.random() * 42),
        tone: monkTone ?? 1,
        seed: (Date.now() % 100000) + 1,
        regions: REGION_LAYOUT.length,
        // A little motion and mains flicker: a perfectly still subject would
        // make the chrominance layer look better than it is.
        motion: 0.0006,
        flicker: 0.0015,
      });
    }

    setLive({ ...IDLE, state: ScanState.ACQUIRING });

    const tick = (now) => {
      const session = sessionRef.current;
      if (!session) return;

      const dt = Math.min((now - lastRef.current) / 1000, 0.2);
      lastRef.current = now;

      const rate = source === 'camera' ? 1 : SIMULATION_SPEED;
      carryRef.current += dt * CAPTURE_FPS * rate;

      let quality = null;
      let guard = 0;
      while (carryRef.current >= 1 && guard < 60 && running(session.state)) {
        carryRef.current -= 1;
        guard += 1;

        let frame = null;
        if (source === 'camera') {
          const cam = cameraRef.current;
          if (!cam) break;
          // Re-locate the face out of band; the detector is async and we do not
          // want to stall the sampling loop waiting for it.
          cam.detectFace();
          const s = cam.sample();
          if (!s) break;
          frame = s.frame;
          quality = s.quality;
          boxesRef.current = cam.regionBoxes();
        } else {
          frame = faceRef.current.nextFrame();
        }
        if (!frame) break;

        // The trace shows the light coming back from the best-perfused region,
        // before any processing. It is the raw thing, not a cleaned-up version.
        pushTrace(frame[0].g);
        session.pushFrame(frame, quality ?? {});
      }

      if (quality) {
        const s = smoothRef.current;
        s.luminance = s.luminance === null ? quality.luminance : s.luminance * 0.9 + quality.luminance * 0.1;
        s.motion = s.motion === null ? quality.motion : s.motion * 0.8 + quality.motion * 0.2;
      }

      const reading = session.latest;
      const total = session.length + session.rejected;

      setLive({
        // While the loop is alive, report ACQUIRING even on the first frames
        // when the session is still technically IDLE — otherwise the Start
        // button flickers back to life for one frame at the top of every scan.
        state: session.state === ScanState.IDLE ? ScanState.ACQUIRING : session.state,
        seconds: session.seconds,
        progress: session.progress,
        samples: session.length,
        rejected: session.rejected,
        acceptance: total ? session.length / total : 1,
        bpm: reading?.bpm ?? null,
        prominence: reading?.prominence ?? null,
        harmonic: reading?.harmonic ?? null,
        agreement: reading?.agreement ?? null,
        spreadBpm: reading?.spreadBpm ?? null,
        methodsAgree: reading?.methodsAgree ?? null,
        methodSpreadBpm: reading?.methodSpreadBpm ?? null,
        byMethod: reading?.byMethod ?? {},
        perRegion: reading?.perRegion ?? [],
        perfusion:
          reading && session.length >= 64
            ? chrominancePerfusion(session.views()[0], reading.primary)
            : null,
        result: session.result,
        advice: quality?.advice ?? OK_ADVICE,
        faceTracked: quality?.faceTracked ?? false,
        luminance: smoothRef.current.luminance,
        motion: smoothRef.current.motion,
      });

      if (running(session.state)) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = 0;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [monkTone, pushTrace, source]);

  const cancel = useCallback(() => {
    stopLoop();
    sessionRef.current = null;
    traceLenRef.current = 0;
    setLive(IDLE);
  }, [stopLoop]);

  /**
   * The diagnostics panel: what each extraction family currently believes, and
   * whether they agree. This is the honest version of a confidence score — not
   * one number to be trusted, but the disagreement itself, shown.
   */
  const diagnostics = useMemo(() => {
    const methods = Object.entries(live.byMethod).map(([method, v]) => ({
      method,
      label: method === 'pos' ? 'Chrominance (POS)' : 'Green channel',
      bpm: v.bpm,
      samples: v.samples,
    }));
    return {
      methods,
      agree: live.methodsAgree,
      spreadBpm: live.methodSpreadBpm,
      regions: live.perRegion.map((r) => ({
        method: r.method,
        name: REGION_LAYOUT[r.region]?.name ?? `region ${r.region + 1}`,
        bpm: r.bpm === null ? null : Number(r.bpm.toFixed(1)),
        prominence: Number((r.prominence ?? 0).toFixed(2)),
      })),
    };
  }, [live.byMethod, live.methodsAgree, live.methodSpreadBpm, live.perRegion]);

  return {
    live,
    error,
    cameraReady,
    diagnostics,
    faceTracking: cameraRef.current?.hasFaceTracking ?? false,
    scanning: live.state === ScanState.ACQUIRING,
    finished: live.state === ScanState.LOCKED || live.state === ScanState.NO_SIGNAL,
    start,
    cancel,
    enableCamera,
    getTrace,
    getRegionBoxes,
    getSpectrum,
  };
}
